import sharedSupabase from '../config/supabase';
import { requestGroupComparisonDrain } from './comparisonRecomputeService';

/**
 * Item Relationship Service (web)
 *
 * Data access for the item_relationships graph — the typed, non-destructive
 * complement to cross-reference merge suggestions. Where a cross-reference
 * suggestion proposes "same product; merge and archive one", a relationship
 * row records what two DIFFERENT canonical items are to each other
 * (same_item via another vendor, purchasing_alternative, same_family,
 * not_related) with the material spec differences preserved. Confirming a
 * relationship NEVER merges items.
 *
 * The backing table/RPCs (item_relationships, record_item_relationship,
 * resolve_item_relationship) and the items-ai-match classify_relationships
 * action ship in a migration that may not be deployed everywhere yet — every
 * call degrades gracefully (console.warn + null/[]) when the schema is
 * missing instead of crashing the UI.
 *
 * Every function takes an optional trailing `client` (defaults to the shared
 * supabase singleton) so tests can inject a fake.
 */

// PostgREST/Postgres "not deployed yet" signatures — same predicate as
// itemCrossReferenceService.
const isMissingSchemaError = (error) => {
  const code = String(error?.code || '');
  const message = String(error?.message || '');
  return (
    ['PGRST202', 'PGRST205', '42883', '42P01'].includes(code) ||
    /could not find the (function|table)|schema cache|does not exist/i.test(message)
  );
};

const warnMissingSchema = (what, error) => {
  console.warn(`[item-relationships] ${what} unavailable (migration not deployed?):`, error?.message);
};

// Include both endpoint names in the same request. The parent inventory list
// can be stale immediately after an import and normally omits archived items;
// neither case should reduce a relationship card to a raw numeric item id.
const RELATIONSHIP_SELECT = `
  *,
  item_a:inventory_items!item_relationships_item_a_id_fkey(id, description, category, location, vendor, is_active),
  item_b:inventory_items!item_relationships_item_b_id_fkey(id, description, category, location, vendor, is_active)
`;

// Treat the database check as the last line of defense, not the first. Old or
// malformed rows must not leak through a read path even if they predate the
// normalized-pair constraint, and a relationship attached to an archived
// merge endpoint is not a relationship between two live canonical items.
export const isLiveCanonicalRelationship = (row) => (
  row
  && row.item_a_id !== null
  && row.item_a_id !== undefined
  && row.item_b_id !== null
  && row.item_b_id !== undefined
  && String(row.item_a_id) !== String(row.item_b_id)
  && row.item_a?.is_active !== false
  && row.item_b?.is_active !== false
);

const validRows = (rows) => (Array.isArray(rows) ? rows : []).filter(isLiveCanonicalRelationship);

// jsonb RPC results arrive already parsed on PostgREST v10+ but as a string
// on older versions — accept both.
const parseJsonb = (data) => {
  if (data === null || data === undefined) return null;
  return typeof data === 'string' ? JSON.parse(data) : data;
};

/**
 * Every non-rejected relationship for a store, newest first. Rejected rows
 * are a stored human "no" — they exist to suppress re-suggestion, never to
 * be listed. Returns [] when the migration is not deployed.
 */
export async function fetchRelationshipsForStore(storeId, client = sharedSupabase) {
  if (!storeId) return [];
  const { data, error } = await client
    .from('item_relationships')
    .select(RELATIONSHIP_SELECT)
    .eq('store_id', storeId)
    .neq('status', 'rejected')
    .order('created_at', { ascending: false });
  if (error) {
    if (isMissingSchemaError(error)) {
      warnMissingSchema('item_relationships', error);
      return [];
    }
    throw new Error(error.message);
  }
  return validRows(data);
}

/**
 * Non-rejected relationships touching one item — the item may sit on either
 * side of the normalized (item_a_id < item_b_id) pair. Returns [] when the
 * migration is not deployed.
 */
export async function fetchRelationshipsForItem(storeId, itemId, client = sharedSupabase) {
  if (!storeId || !itemId) return [];
  const { data, error } = await client
    .from('item_relationships')
    .select(RELATIONSHIP_SELECT)
    .eq('store_id', storeId)
    .or(`item_a_id.eq.${itemId},item_b_id.eq.${itemId}`)
    .neq('status', 'rejected')
    .order('created_at', { ascending: false });
  if (error) {
    if (isMissingSchemaError(error)) {
      warnMissingSchema('item_relationships', error);
      return [];
    }
    throw new Error(error.message);
  }
  return validRows(data);
}

async function resolveRelationship(relationshipId, action, typeOverride, client) {
  if (!relationshipId) throw new Error('relationship id is required');
  const { data, error } = await client.rpc('resolve_item_relationship', {
    p_relationship_id: relationshipId,
    p_action: action,
    p_relationship_type: typeOverride ?? null,
  });
  if (error) {
    if (isMissingSchemaError(error)) {
      warnMissingSchema('resolve_item_relationship', error);
      return null;
    }
    throw new Error(error.message);
  }
  const row = parseJsonb(data);
  // The resolve just changed (or may have changed) equivalent membership;
  // database triggers enqueued the group recompute — this fire-and-forget
  // nudge only drains it sooner. Never awaited, never fails the resolve.
  if (row) void requestGroupComparisonDrain(client);
  return row;
}

/**
 * Confirms a relationship (optionally reclassifying it — the RPC withdraws
 * merge safety when the override moves it away from same_item). Confirming
 * records the relationship; it never merges items. Returns the updated row
 * (parsed jsonb), or null when the migration is not deployed.
 */
export async function confirmRelationship(relationshipId, typeOverride = null, client = sharedSupabase) {
  return resolveRelationship(relationshipId, 'confirm', typeOverride, client);
}

/**
 * Rejects a relationship ("not related"). The row persists as a negative so
 * the pair — in either direction — is not re-suggested. Returns the updated
 * row (parsed jsonb), or null when the migration is not deployed.
 */
export async function rejectRelationship(relationshipId, client = sharedSupabase) {
  return resolveRelationship(relationshipId, 'reject', null, client);
}

/**
 * Creates (or deliberately restores) a confirmed relationship between two
 * live canonical inventory items. The RPC performs the authoritative store,
 * liveness, self-pair, type, normalization, and duplicate checks; this client
 * guard keeps an obvious A -> A request off the wire as well.
 */
export async function createManualRelationship(
  storeId,
  sourceItemId,
  targetItemId,
  relationshipType,
  client = sharedSupabase,
) {
  if (!storeId || sourceItemId === null || sourceItemId === undefined
    || targetItemId === null || targetItemId === undefined) {
    throw new Error('store, source item, and target item are required');
  }
  if (String(sourceItemId) === String(targetItemId)) {
    throw new Error('An item cannot be related to itself');
  }
  const { data, error } = await client.rpc('create_item_relationship', {
    p_store_id: storeId,
    p_source_item_id: sourceItemId,
    p_target_item_id: targetItemId,
    p_relationship_type: relationshipType,
  });
  if (error) {
    if (isMissingSchemaError(error)) {
      warnMissingSchema('create_item_relationship', error);
      return null;
    }
    throw new Error(error.message);
  }
  const row = parseJsonb(data);
  // Same fire-and-forget drain nudge as resolveRelationship: a manual
  // creation may have just extended an equivalent group.
  if (row) void requestGroupComparisonDrain(client);
  return row;
}

/**
 * Asks the items-ai-match edge function to classify one item against its
 * deterministically retrieved catalog neighbors (action
 * 'classify_relationships'), persisting 'suggested' item_relationships rows
 * server-side. Returns the function's response ({ relationships, ... }), or
 * null when the function is unavailable — the relationship UI is additive
 * and must never break the page.
 */
export async function requestClassification(storeId, itemId, client = sharedSupabase) {
  if (!storeId || !itemId) return null;
  try {
    const { data, error } = await client.functions.invoke('items-ai-match', {
      body: { action: 'classify_relationships', store_id: storeId, item_id: itemId },
    });
    if (error) throw new Error(error.message || 'items-ai-match invoke failed');
    return data ?? null;
  } catch (err) {
    console.warn('[item-relationships] classification unavailable (edge function not deployed?):', err?.message || err);
    return null;
  }
}

/**
 * Stored group-level comparison records (equivalent_group_comparisons) for a
 * store, keyed by group_key as a string. group_key follows the client's own
 * convention (buildWorkingSetGroups): the smallest live member id, so the two
 * sides agree on which row describes which group.
 *
 * The table ships in migration 20260819120000 and may be empty or missing
 * entirely; both degrade to {} so canonical resolution can fall back to
 * option recency (see relatedProductCatalog.resolveCanonicalMemberId). Never
 * throws — the catalog must render whether or not the group layer has run.
 */
export async function fetchEquivalentGroupComparisons(storeId, client = sharedSupabase) {
  if (!storeId) return {};
  try {
    const { data, error } = await client
      .from('equivalent_group_comparisons')
      .select('group_key, canonical_item_id, comparison_unit, comparison_dimension, status, member_item_ids, computed_at')
      .eq('store_id', storeId);
    if (error) {
      if (isMissingSchemaError(error)) {
        warnMissingSchema('equivalent_group_comparisons', error);
      } else {
        console.warn('[item-relationships] equivalent_group_comparisons read failed:', error.message);
      }
      return {};
    }
    const byGroupKey = {};
    (Array.isArray(data) ? data : []).forEach((row) => {
      if (row?.group_key === null || row?.group_key === undefined) return;
      byGroupKey[String(row.group_key)] = row;
    });
    return byGroupKey;
  } catch (err) {
    console.warn('[item-relationships] equivalent_group_comparisons unavailable:', err?.message || err);
    return {};
  }
}
